import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { QUERIES } from '@/constants/queries'
import { ROUTES } from '@/constants/routes'
import { getAllCoursesByCurriculum } from '@/services/education/get-all-courses-by-curriculum'
import { getRequestById } from '@/services/requests/get-request-by-id'
import { startRequestReview } from '@/services/requests/start-request-review'
import type { Curriculum } from '@/types/courses/curriculum'

export const useRequestTypeCreditTransfer = () => {
  const [openReviewModal, setOpenReviewModal] = useState(false)
  const [openSuccessModal, setOpenSuccesModal] = useState(false)
  const [currentCurriculum, setCurrentCurriculum] = useState<Curriculum>()
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: requestData, isLoading: isRequestLoading } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUEST_BY_ID, id],
    queryFn: () => getRequestById(id!),
    enabled: !!id
  })

  const { data: curriculumCourses, isLoading: isCurriculumCoursesLoading } =
    useQuery({
      queryKey: [QUERIES.QUERY_GET_COURSES_BY_CURRICULUM, currentCurriculum],
      queryFn: () => getAllCoursesByCurriculum(currentCurriculum?.id),
      enabled: !!currentCurriculum
    })
  const handleSuccess = () => {
    setOpenReviewModal(false)
    setOpenSuccesModal(true)
  }

  const handleSetSuccesModal = (value: boolean) => {
    if (!value) {
      navigateToMenu()
    }

    setOpenSuccesModal(value)
  }

  const handleMutate = () => {
    if (!isPendingStartReview) {
      mutateStartReview()
    }
  }

  const navigateToMenu = () => {
    setOpenSuccesModal(false)
    navigate(`/${ROUTES.CREDIT_TRANSFER.route}`)
  }

  const navigateToThisReview = () => {
    setOpenSuccesModal(false)
    const path = generatePath(ROUTES.REVIEW_TYPES_CREDIT_TRANSFER.route, {
      id
    })
    navigate(`/${path}`)
  }

  const { mutate: mutateStartReview, isPending: isPendingStartReview } =
    useMutation({
      mutationKey: [QUERIES.QUERY_PATCH_START_REQUEST_REVIEW],
      mutationFn: () => startRequestReview(id!),
      onSuccess: () => handleSuccess()
    })

  const isLoading = isRequestLoading || isCurriculumCoursesLoading

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (requestData) {
      const curriculum = requestData.desiredEducation.curriculum
      setCurrentCurriculum({ id: curriculum, curriculumID: curriculum })
    }
  }, [requestData, setCurrentCurriculum])

  return {
    states: {
      requestData,
      isLoading,
      isPendingStartReview,
      openReviewModal,
      openSuccessModal,
      currentCurriculum,
      curriculumCourses
    },
    actions: {
      handleMutate,
      setOpenReviewModal,
      handleSetSuccesModal,
      navigateToMenu,
      navigateToThisReview
    }
  }
}
